import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  IoCloudUploadOutline,
  IoTrashOutline,
  IoAttach,
} from "react-icons/io5";
import "./Assets.css";

// --- Environment Variables ---
const AUTH_TOKEN_KEY = "zTaskAuthToken";
const ASSET_SERVICE_BASE_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
const NODE_SERVICE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;

// --- API Endpoint Constructors ---
const ASSET_API_URL = `${ASSET_SERVICE_BASE_URL}/api`;
const ATTACHMENT_API_URL = NODE_SERVICE_BASE_URL;
const TOOLS_API_URL = `${NODE_SERVICE_BASE_URL}/api`;

// --- API & Auth Logic ---
const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
const getApiHeaders = (isMultipart = false) => {
  const token = getAuthToken();
  if (!token) throw new Error("Authentication token not found.");
  const headers = { Authorization: `Bearer ${token}` };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
    headers["Accept"] = "application/json";
  }
  return headers;
};

// --- API Functions ---

// THIS IS THE NEW FUNCTION to fetch all users for the ID-to-Name mapping
const fetchAllUsersForMappingAPI = async () => {
  const response = await fetch(`${TOOLS_API_URL}/get-column/users/name`, {
    headers: getApiHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch users list: ${response.statusText}`);
  }
  const data = await response.json();
  // The API returns [{ id, value }], but the component expects [{ id, name }] for the userMap.
  // We will transform the data here to match what the rest of the code expects.
  if (Array.isArray(data)) {
    return data.map((user) => ({
      id: user.id,
      name: user.value,
    }));
  }
  return []; // Return an empty array if the response is not as expected
};

const deleteAttachmentAPI = async (attachmentId) => {
  const response = await fetch(
    `${ATTACHMENT_API_URL}/attachment/api/attachments/${attachmentId}`,
    {
      method: "DELETE",
      headers: getApiHeaders(),
    }
  );
  if (!response.ok && response.status !== 204) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to delete attachment");
  }
  return true;
};

const Assets = () => {
  const initialFormData = {
    asset_code: "",
    name: "",
    description: "",
    year_of_purchase: "",
    year_of_manufacture: "",
    registration_number: "",
    serial_number: "",
    asset_type: "",
    capacity_mt: "",
    purchase_cost: "",
    written_down_value: "",
    date_of_purchase: "",
    supplier: "",
    currency: "",
    import_configuration: "",
    is_sold: false,
    avg_km_per_liter: "",
    avg_hr_per_liter: "",
    image_url: "",
    active: true,
  };

  // --- State Management ---
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingAssetTypes, setLoadingAssetTypes] = useState(true);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [assetsList, setAssetsList] = useState([]);
  const [loadingAssetsList, setLoadingAssetsList] = useState(true);
  const [formMode, setFormMode] = useState("create");
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const formRef = useRef(null);

  const userMap = useMemo(
    () =>
      (users || []).reduce((acc, user) => {
        acc[user.id] = user.name;
        return acc;
      }, {}),
    [users]
  );

  // --- File Handlers ---
  const addFilesAndDescriptions = (newFiles) => {
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setFileDescriptions((prev) => [
      ...prev,
      ...Array(newFiles.length).fill(""),
    ]);
  };
  const handleFileChange = (e) => {
    if (e.target.files) {
      addFilesAndDescriptions(Array.from(e.target.files));
    }
    e.target.value = null;
  };
  const handleRemoveNewFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
    setFileDescriptions((prev) => prev.filter((_, i) => i !== indexToRemove));
  };
  const handleDescriptionChange = (index, value) => {
    const newDescriptions = [...fileDescriptions];
    newDescriptions[index] = value;
    setFileDescriptions(newDescriptions);
  };
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      addFilesAndDescriptions(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  }, []);
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length) setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const showAuthError = () =>
    setSubmitStatus({
      type: "error",
      message: "Authentication error. Please log in again.",
    });

  const fetchAssets = async () => {
    setLoadingAssetsList(true);
    try {
      const response = await fetch(`${ASSET_API_URL}/assets/`, {
        headers: getApiHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAssetsList(
          Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : []
        );
      } else {
        setSubmitStatus({
          type: "error",
          message: "Failed to load assets list.",
        });
        setAssetsList([]);
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "An error occurred while loading assets.",
      });
      setAssetsList([]);
    } finally {
      setLoadingAssetsList(false);
    }
  };

  useEffect(() => {
    if (!getAuthToken()) {
      showAuthError();
      setLoadingAssetsList(false);
      setLoadingAssetTypes(false);
      setLoadingCurrencies(false);
      return;
    }
    fetchAssets();
    const fetchDropdownData = async (column, setData, setLoading) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${TOOLS_API_URL}/get-column/common_master/${column}`,
          { headers: getApiHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          setData(
            Array.isArray(data)
              ? data.map((item) => ({ id: item.id, name: item.value }))
              : []
          );
        } else {
          setData([]);
        }
      } catch (error) {
        console.error(`Error fetching ${column}:`, error);
      } finally {
        setLoading(false);
      }
    };
    const loadInitialData = async () => {
      Promise.all([
        fetchDropdownData("asset_type", setAssetTypes, setLoadingAssetTypes),
        fetchDropdownData("currency", setCurrencies, setLoadingCurrencies),
        // UPDATED this part to use the new API function
        fetchAllUsersForMappingAPI()
          .then((mappedUsers) => setUsers(mappedUsers))
          .catch((err) => {
            console.error(
              "Failed to fetch user list. 'Added by' column may show IDs.",
              err
            );
            setUsers([]); // Fallback to an empty array on error
          }),
      ]);
    };
    loadInitialData();
  }, []);

  const displayedAssets = useMemo(() => {
    if (!Array.isArray(assetsList)) return [];
    if (!searchTerm) return assetsList.slice(0, 10);
    const lowercasedFilter = searchTerm.toLowerCase();
    return assetsList.filter((asset) =>
      Object.values(asset).some((value) =>
        String(value).toLowerCase().includes(lowercasedFilter)
      )
    );
  }, [assetsList, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "is_sold" || name === "active" ? val === "true" : val,
    }));
  };

  const resetFormState = () => {
    setFormData(initialFormData);
    setSelectedFiles([]);
    setFileDescriptions([]);
    setExistingAttachments([]);
    setSubmitStatus(null);
    setFormMode("create");
    setEditingAssetId(null);
    const fileInput = document.getElementById("image_upload");
    if (fileInput) fileInput.value = "";
  };

  const handleShowCreateForm = () => {
    resetFormState();
    setIsFormVisible(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      100
    );
  };

  const handleEditClick = async (asset) => {
    resetFormState();
    setFormMode("edit");
    setEditingAssetId(asset.id);
    setFormData({
      ...initialFormData,
      ...asset,
      date_of_purchase: asset.date_of_purchase
        ? asset.date_of_purchase.split("T")[0]
        : "",
      active: asset.active === undefined ? true : asset.active,
    });
    setIsFormVisible(true);
    try {
      const response = await fetch(
        `${ATTACHMENT_API_URL}/attachment/api/attachments/${asset.id}/Assets`,
        { headers: getApiHeaders() }
      );
      if (response.ok) {
        const attachments = await response.json();
        setExistingAttachments(attachments.filter((att) => att.active));
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      100
    );
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await deleteAttachmentAPI(attachmentId);
      setExistingAttachments((prev) =>
        prev.filter((att) => att.id !== attachmentId)
      );
      setSubmitStatus({ type: "success", message: "File deleted." });
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      setSubmitStatus({ type: "error", message: error.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus(null);
    const uploadFiles = async (assetId) => {
      if (selectedFiles.length === 0) return;
      const uploadFormData = new FormData();
      selectedFiles.forEach((file) => uploadFormData.append("files", file));
      fileDescriptions.forEach((desc) =>
        uploadFormData.append("descriptions[]", desc || "")
      );
      try {
        const response = await fetch(
          `${ATTACHMENT_API_URL}/attachment/api/attachments/${assetId}/Assets`,
          { method: "POST", headers: getApiHeaders(true), body: uploadFormData }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "File upload failed.");
        }
        setSelectedFiles([]);
        setFileDescriptions([]);
      } catch (uploadError) {
        setSubmitStatus({
          type: "error",
          message: `Asset saved, but file upload failed: ${uploadError.message}`,
        });
        throw uploadError;
      }
    };
    const payload = {
      ...formData,
      year_of_purchase: formData.year_of_purchase
        ? Number(formData.year_of_purchase)
        : null,
      year_of_manufacture: formData.year_of_manufacture
        ? Number(formData.year_of_manufacture)
        : null,
      capacity_mt: formData.capacity_mt ? Number(formData.capacity_mt) : null,
      purchase_cost: formData.purchase_cost
        ? Number(formData.purchase_cost)
        : null,
      written_down_value: formData.written_down_value
        ? Number(formData.written_down_value)
        : null,
      date_of_purchase: formData.date_of_purchase || null,
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null) {
        delete payload[key];
      }
    });
    let assetId = formMode === "edit" ? editingAssetId : null;
    try {
      if (formMode === "create") {
        const response = await fetch(`${ASSET_API_URL}/assets/`, {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify(payload),
        });
        const responseData = await response.json();
        if (!response.ok || response.status !== 201)
          throw new Error(
            responseData.message ||
              responseData.detail ||
              "Failed to create asset."
          );
        assetId = responseData.id;
      } else {
        const response = await fetch(
          `${ASSET_API_URL}/assets/${editingAssetId}`,
          {
            method: "PUT",
            headers: getApiHeaders(),
            body: JSON.stringify(payload),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || errorData.detail || "Failed to update asset."
          );
        }
      }
      if (selectedFiles.length > 0) {
        await uploadFiles(assetId);
      }
      setSubmitStatus({
        type: "success",
        message: `Asset ${
          formMode === "create" ? "created" : "updated"
        } successfully!`,
      });
      resetFormState();
      await fetchAssets();
      setIsFormVisible(false);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: `Submission failed: ${error.message}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormActionCancel = () => {
    setIsFormVisible(false);
    resetFormState();
  };
  const getFileNameFromUrl = (url) => {
    try {
      return decodeURIComponent(url.split("/").pop());
    } catch {
      return url;
    }
  };
  const formatDate = (dateString) =>
    dateString ? dateString.split("T")[0] : "N/A";
  const exportToCsv = (assetsToExport, filenamePrefix) => {
    if (assetsToExport.length === 0) {
      alert("No data available to export.");
      return;
    }
    const sanitize = (field) => {
      const str = String(field ?? "");
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const headers = [
      "ID",
      "Asset Code",
      "Name",
      "Asset Type",
      "Description",
      "Reg Number",
      "Serial Number",
      "Year Purchase",
      "Year Manufacture",
      "Capacity (MT)",
      "Purchase Cost",
      "WDV",
      "Purchase Date",
      "Supplier",
      "Currency",
      "Avg KM/L",
      "Avg HR/L",
      "Is Sold",
      "Active",
    ];
    const csvRows = assetsToExport.map((a) =>
      [
        a.id,
        a.asset_code,
        a.name,
        a.asset_type,
        a.description,
        a.registration_number,
        a.serial_number,
        a.year_of_purchase,
        a.year_of_manufacture,
        a.capacity_mt,
        a.purchase_cost,
        a.written_down_value,
        formatDate(a.date_of_purchase),
        a.supplier,
        a.currency,
        a.avg_km_per_liter,
        a.avg_hr_per_liter,
        a.is_sold ? "Yes" : "No",
        a.active ? "Active" : "Inactive",
      ]
        .map(sanitize)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filenamePrefix}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleExportFiltered = () =>
    exportToCsv(displayedAssets, "assets_export_filtered");
  const handleExportAll = () => exportToCsv(assetsList, "assets_export_all");

  // The rest of the component remains unchanged
  return (
    <div className="assets-container">
      <div className="assets-list-container">
        <div className="list-header">
          <h2 className="assets-list-title">
            {searchTerm ? "Search Results" : "Recently Created Assets"}
          </h2>
          <div className="list-actions">
            <button onClick={handleShowCreateForm} className="btn-action">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                width="20"
                height="20"
              >
                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
              </svg>
              Create New Asset
            </button>
            <button
              onClick={handleExportFiltered}
              className="btn-action"
              disabled={loadingAssetsList || displayedAssets.length === 0}
              title="Export currently visible results"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                width="20"
                height="20"
              >
                <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
              </svg>
              Export Filtered
            </button>
            <button
              onClick={handleExportAll}
              className="btn-action"
              disabled={loadingAssetsList || assetsList.length === 0}
              title="Export all assets"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                width="20"
                height="20"
              >
                <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
              </svg>
              Export All
            </button>
          </div>
        </div>
        <div className="list-search-bar">
          <input
            type="text"
            placeholder="Search all columns..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {submitStatus && (
          <div className={`status-message ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}
        {loadingAssetsList ? (
          <p>Loading assets...</p>
        ) : (
          <div className="table-wrapper">
            <table className="assets-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Asset Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Reg. No</th>
                  <th>Serial No</th>
                  <th className="description-cell">Description</th>
                  <th>Cost</th>
                  <th>Purchase Date</th>
                </tr>
              </thead>
              <tbody>
                {displayedAssets.length > 0 ? (
                  displayedAssets.map((asset) => (
                    <tr key={asset.id}>
                      <td>
                        <button
                          onClick={() => handleEditClick(asset)}
                          className="btn-action btn-action-sm"
                          disabled={isLoading}
                        >
                          Edit
                        </button>
                      </td>
                      <td>{asset.asset_code || "N/A"}</td>
                      <td>{asset.name || "N/A"}</td>
                      <td>{asset.asset_type || "N/A"}</td>
                      <td>
                        <span
                          className={
                            asset.active ? "status-active" : "status-inactive"
                          }
                        >
                          {asset.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{asset.registration_number || "N/A"}</td>
                      <td>{asset.serial_number || "N/A"}</td>
                      <td className="description-cell">
                        {asset.description || "N/A"}
                      </td>
                      <td>
                        {asset.purchase_cost
                          ? `${
                              asset.currency || ""
                            } ${asset.purchase_cost.toLocaleString()}`
                          : "N/A"}
                      </td>
                      <td>{formatDate(asset.date_of_purchase)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">
                      {searchTerm
                        ? "No assets match your search."
                        : "No assets found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isFormVisible && (
        <>
          <header className="assets-header" ref={formRef}>
            <h1 className="assets-title">
              {formMode === "create" ? "Create New Asset" : "Edit Asset"}
            </h1>
            <p className="assets-subtitle">
              {formMode === "create"
                ? "Fill in the details below to add a new asset."
                : `You are editing: ${formData.name || ""}`}
            </p>
          </header>
          <div className="assets-form-container">
            <form onSubmit={handleSubmit} className="assets-form" noValidate>
              <div className="form-grid">
                {/* All form fields are untouched */}
                <div className="form-group">
                  <label htmlFor="asset_code" className="form-label">
                    Asset Code <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="asset_code"
                    name="asset_code"
                    value={formData.asset_code}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                    placeholder="e.g., ASSET-001"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Asset Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                    placeholder="Enter Asset Name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="asset_type" className="form-label">
                    Asset Type <span className="required-asterisk">*</span>
                  </label>
                  <select
                    id="asset_type"
                    name="asset_type"
                    value={formData.asset_type}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={isLoading || loadingAssetTypes}
                  >
                    <option value="" disabled>
                      {loadingAssetTypes ? "Loading..." : "Select Type"}
                    </option>
                    {assetTypes.map((type) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group grid-column-span-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows="3"
                    disabled={isLoading}
                    placeholder="Provide a brief description"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="registration_number" className="form-label">
                    Registration Number{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="registration_number"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    required
                    placeholder="e.g., MH12AB3456"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="serial_number" className="form-label">
                    Serial Number <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="serial_number"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    required
                    placeholder="e.g., SN-12345XYZ"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="year_of_manufacture" className="form-label">
                    Year of Manufacture{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="year_of_manufacture"
                    name="year_of_manufacture"
                    value={formData.year_of_manufacture}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    required
                    placeholder="e.g., 2020"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="year_of_purchase" className="form-label">
                    Year of Purchase{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="year_of_purchase"
                    name="year_of_purchase"
                    value={formData.year_of_purchase}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    placeholder="e.g., 2021"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="purchase_cost" className="form-label">
                    Purchase Cost <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="purchase_cost"
                    name="purchase_cost"
                    value={formData.purchase_cost}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                    placeholder="e.g., 500000"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={isLoading || loadingCurrencies}
                  >
                    <option value="" disabled>
                      {loadingCurrencies ? "Loading..." : "Select"}
                    </option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="date_of_purchase" className="form-label">
                    Date of Purchase{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    id="date_of_purchase"
                    name="date_of_purchase"
                    value={formData.date_of_purchase}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="written_down_value" className="form-label">
                    Written Down Value (WDV)
                  </label>
                  <input
                    type="number"
                    id="written_down_value"
                    name="written_down_value"
                    value={formData.written_down_value}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    placeholder="e.g., 450000"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="supplier" className="form-label">
                    Supplier <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="supplier"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    required
                    placeholder="e.g., ABC Corporation"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="capacity_mt" className="form-label">
                    Capacity (MT) <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="capacity_mt"
                    name="capacity_mt"
                    value={formData.capacity_mt}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isLoading}
                    required
                    placeholder="e.g., 20.5"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="avg_km_per_liter" className="form-label">
                    Avg KM/Liter<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="avg_km_per_liter"
                    name="avg_km_per_liter"
                    value={formData.avg_km_per_liter}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                    placeholder="e.g., 15.5"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="avg_hr_per_liter" className="form-label">
                    Avg HR/Liter<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="avg_hr_per_liter"
                    name="avg_hr_per_liter"
                    value={formData.avg_hr_per_liter}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    disabled={isLoading}
                    placeholder="e.g., 2.5"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="is_sold" className="form-label">
                    Is Sold
                  </label>
                  <select
                    id="is_sold"
                    name="is_sold"
                    value={formData.is_sold.toString()}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="active" className="form-label">
                    Active Status
                  </label>
                  <select
                    id="active"
                    name="active"
                    value={formData.active.toString()}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={isLoading}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div className="form-group grid-column-span-3">
                  {formMode === "edit" && (
                    <div className="attachment-section">
                      <h3 className="attachment-section-title">
                        Manage Existing Attachments
                      </h3>
                      {existingAttachments.length > 0 ? (
                        <div className="attachments-table-container">
                          <table className="attachments-table">
                            <thead>
                              <tr>
                                <th>File Name</th>
                                <th>Description</th>
                                <th>Added By</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {existingAttachments.map((att) => (
                                <tr key={att.id}>
                                  <td>
                                    <a
                                      href={att.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      <IoAttach />{" "}
                                      {getFileNameFromUrl(att.file_url)}
                                    </a>
                                  </td>
                                  <td>{att.description || ""}</td>
                                  <td>
                                    {userMap[att.added_by] ||
                                      `ID: ${att.added_by}`}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="remove-file-btn"
                                      onClick={() =>
                                        handleDeleteExistingAttachment(att.id)
                                      }
                                      title="Remove attachment"
                                      disabled={isLoading}
                                    >
                                      <IoTrashOutline />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="no-attachments-message">
                          No existing attachments.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="attachment-section">
                    <h3 className="attachment-section-title">
                      {formMode === "edit"
                        ? "Add New Attachments"
                        : "Attachments"}
                    </h3>
                    <label
                      htmlFor="image_upload"
                      className={`drop-zone ${isDragging ? "drag-over" : ""}`}
                      onDrop={handleFileDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                    >
                      <input
                        id="image_upload"
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="file-input-hidden"
                        disabled={isLoading}
                      />
                      <div className="drop-zone-content">
                        <IoCloudUploadOutline />{" "}
                        <span>
                          {isDragging
                            ? "Drop files here"
                            : "Drag & Drop or Click to Upload"}
                        </span>
                      </div>
                    </label>
                    {selectedFiles.length > 0 && (
                      <div className="file-description-list">
                        <h4 className="file-list-header">
                          New Files to Upload
                        </h4>
                        {selectedFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="file-description-item"
                          >
                            <span className="file-name-label" title={file.name}>
                              {file.name}
                            </span>
                            <input
                              type="text"
                              value={fileDescriptions[index]}
                              onChange={(e) =>
                                handleDescriptionChange(index, e.target.value)
                              }
                              placeholder="Enter a brief description..."
                              className="file-description-input"
                              disabled={isLoading}
                            />
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => handleRemoveNewFile(index)}
                              title="Remove file"
                              disabled={isLoading}
                            >
                              <IoTrashOutline />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleFormActionCancel}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  {formMode === "create" ? "Reset" : "Cancel Edit"}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      {" "}
                      <span className="loading-spinner"></span>Submitting...
                    </>
                  ) : formMode === "create" ? (
                    "Create Asset"
                  ) : (
                    "Update Asset"
                  )}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Assets;













// import React, {
//   useState,
//   useEffect,
//   useRef,
//   useMemo,
//   useCallback,
// } from "react";
// import {
//   IoCloudUploadOutline,
//   IoTrashOutline,
//   IoAttach,
// } from "react-icons/io5";
// import "./Assets.css";

// // --- Environment Variables ---
// const AUTH_TOKEN_KEY = "zTaskAuthToken";
// const ASSET_SERVICE_BASE_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
// const NODE_SERVICE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;

// // --- API Endpoint Constructors ---
// const ASSET_API_URL = `${ASSET_SERVICE_BASE_URL}/api`;
// const ATTACHMENT_API_URL = NODE_SERVICE_BASE_URL;
// const TOOLS_API_URL = `${NODE_SERVICE_BASE_URL}/api`;

// // --- Cache Configuration ---
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
// const CACHE_KEYS = {
//   ASSETS: 'assets_cache',
//   ASSET_TYPES: 'asset_types_cache',
//   CURRENCIES: 'currencies_cache',
//   USERS: 'users_cache'
// };

// // --- In-Memory Cache Implementation ---
// const cache = {
//   data: {},
  
//   set(key, value) {
//     this.data[key] = {
//       value,
//       timestamp: Date.now()
//     };
//   },
  
//   get(key) {
//     const cached = this.data[key];
//     if (!cached) return null;
    
//     const isExpired = (Date.now() - cached.timestamp) > CACHE_DURATION;
//     if (isExpired) {
//       delete this.data[key];
//       return null;
//     }
    
//     return cached.value;
//   },
  
//   clear(key) {
//     if (key) {
//       delete this.data[key];
//     } else {
//       this.data = {};
//     }
//   },
  
//   clearAll() {
//     this.data = {};
//   }
// };

// // --- API & Auth Logic ---
// const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
// const getApiHeaders = (isMultipart = false) => {
//   const token = getAuthToken();
//   if (!token) throw new Error("Authentication token not found.");
//   const headers = { Authorization: `Bearer ${token}` };
//   if (!isMultipart) {
//     headers["Content-Type"] = "application/json";
//     headers["Accept"] = "application/json";
//   }
//   return headers;
// };

// // --- API Functions ---

// // THIS IS THE NEW FUNCTION to fetch all users for the ID-to-Name mapping
// const fetchAllUsersForMappingAPI = async (useCache = true) => {
//   if (useCache) {
//     const cached = cache.get(CACHE_KEYS.USERS);
//     if (cached) {
//       console.log('Using cached users data');
//       return cached;
//     }
//   }
  
//   const response = await fetch(`${TOOLS_API_URL}/get-column/users/name`, {
//     headers: getApiHeaders(),
//   });
//   if (!response.ok) {
//     throw new Error(`Failed to fetch users list: ${response.statusText}`);
//   }
//   const data = await response.json();
//   // The API returns [{ id, value }], but the component expects [{ id, name }] for the userMap.
//   // We will transform the data here to match what the rest of the code expects.
//   let transformedData = [];
//   if (Array.isArray(data)) {
//     transformedData = data.map((user) => ({
//       id: user.id,
//       name: user.value,
//     }));
//   }
  
//   // Cache the transformed data
//   if (useCache) {
//     cache.set(CACHE_KEYS.USERS, transformedData);
//   }
  
//   return transformedData;
// };

// const deleteAttachmentAPI = async (attachmentId) => {
//   const response = await fetch(
//     `${ATTACHMENT_API_URL}/attachment/api/attachments/${attachmentId}`,
//     {
//       method: "DELETE",
//       headers: getApiHeaders(),
//     }
//   );
//   if (!response.ok && response.status !== 204) {
//     const err = await response.json().catch(() => ({}));
//     throw new Error(err.message || "Failed to delete attachment");
//   }
//   return true;
// };

// const Assets = () => {
//   const initialFormData = {
//     asset_code: "",
//     name: "",
//     description: "",
//     year_of_purchase: "",
//     year_of_manufacture: "",
//     registration_number: "",
//     serial_number: "",
//     asset_type: "",
//     capacity_mt: "",
//     purchase_cost: "",
//     written_down_value: "",
//     date_of_purchase: "",
//     supplier: "",
//     currency: "",
//     import_configuration: "",
//     is_sold: false,
//     avg_km_per_liter: "",
//     avg_hr_per_liter: "",
//     image_url: "",
//     active: true,
//   };

//   // --- State Management ---
//   const [formData, setFormData] = useState(initialFormData);
//   const [selectedFiles, setSelectedFiles] = useState([]);
//   const [fileDescriptions, setFileDescriptions] = useState([]);
//   const [existingAttachments, setExistingAttachments] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [submitStatus, setSubmitStatus] = useState(null);
//   const [assetTypes, setAssetTypes] = useState([]);
//   const [currencies, setCurrencies] = useState([]);
//   const [users, setUsers] = useState([]);
//   const [loadingAssetTypes, setLoadingAssetTypes] = useState(true);
//   const [loadingCurrencies, setLoadingCurrencies] = useState(true);
//   const [assetsList, setAssetsList] = useState([]);
//   const [loadingAssetsList, setLoadingAssetsList] = useState(true);
//   const [formMode, setFormMode] = useState("create");
//   const [editingAssetId, setEditingAssetId] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [isFormVisible, setIsFormVisible] = useState(false);
//   const [isDragging, setIsDragging] = useState(false);
//   const formRef = useRef(null);

//   const userMap = useMemo(
//     () =>
//       (users || []).reduce((acc, user) => {
//         acc[user.id] = user.name;
//         return acc;
//       }, {}),
//     [users]
//   );

//   // --- File Handlers ---
//   const addFilesAndDescriptions = (newFiles) => {
//     setSelectedFiles((prev) => [...prev, ...newFiles]);
//     setFileDescriptions((prev) => [
//       ...prev,
//       ...Array(newFiles.length).fill(""),
//     ]);
//   };
//   const handleFileChange = (e) => {
//     if (e.target.files) {
//       addFilesAndDescriptions(Array.from(e.target.files));
//     }
//     e.target.value = null;
//   };
//   const handleRemoveNewFile = (indexToRemove) => {
//     setSelectedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
//     setFileDescriptions((prev) => prev.filter((_, i) => i !== indexToRemove));
//   };
//   const handleDescriptionChange = (index, value) => {
//     const newDescriptions = [...fileDescriptions];
//     newDescriptions[index] = value;
//     setFileDescriptions(newDescriptions);
//   };
//   const handleFileDrop = useCallback((e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setIsDragging(false);
//     if (e.dataTransfer.files?.length) {
//       addFilesAndDescriptions(Array.from(e.dataTransfer.files));
//       e.dataTransfer.clearData();
//     }
//   }, []);
//   const handleDragOver = useCallback((e) => {
//     e.preventDefault();
//     e.stopPropagation();
//   }, []);
//   const handleDragEnter = useCallback((e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.dataTransfer.items?.length) setIsDragging(true);
//   }, []);
//   const handleDragLeave = useCallback((e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setIsDragging(false);
//   }, []);
//   const showAuthError = () =>
//     setSubmitStatus({
//       type: "error",
//       message: "Authentication error. Please log in again.",
//     });

//   const fetchAssets = async (useCache = true) => {
//     if (useCache) {
//       const cached = cache.get(CACHE_KEYS.ASSETS);
//       if (cached) {
//         console.log('Using cached assets data');
//         setAssetsList(cached);
//         setLoadingAssetsList(false);
//         return;
//       }
//     }
    
//     setLoadingAssetsList(true);
//     try {
//       const response = await fetch(`${ASSET_API_URL}/assets/`, {
//         headers: getApiHeaders(),
//       });
//       if (response.ok) {
//         const data = await response.json();
//         const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
//         setAssetsList(sortedData);
        
//         // Cache the data
//         if (useCache) {
//           cache.set(CACHE_KEYS.ASSETS, sortedData);
//         }
//       } else {
//         setSubmitStatus({
//           type: "error",
//           message: "Failed to load assets list.",
//         });
//         setAssetsList([]);
//       }
//     } catch (error) {
//       setSubmitStatus({
//         type: "error",
//         message: "An error occurred while loading assets.",
//       });
//       setAssetsList([]);
//     } finally {
//       setLoadingAssetsList(false);
//     }
//   };

//   useEffect(() => {
//     if (!getAuthToken()) {
//       showAuthError();
//       setLoadingAssetsList(false);
//       setLoadingAssetTypes(false);
//       setLoadingCurrencies(false);
//       return;
//     }
//     fetchAssets();
//     const fetchDropdownData = async (column, setData, setLoading, cacheKey) => {
//       // Check cache first
//       const cached = cache.get(cacheKey);
//       if (cached) {
//         console.log(`Using cached ${column} data`);
//         setData(cached);
//         setLoading(false);
//         return;
//       }
      
//       setLoading(true);
//       try {
//         const response = await fetch(
//           `${TOOLS_API_URL}/get-column/common_master/${column}`,
//           { headers: getApiHeaders() }
//         );
//         if (response.ok) {
//           const data = await response.json();
//           const transformedData = Array.isArray(data)
//             ? data.map((item) => ({ id: item.id, name: item.value }))
//             : [];
//           setData(transformedData);
          
//           // Cache the data
//           cache.set(cacheKey, transformedData);
//         } else {
//           setData([]);
//         }
//       } catch (error) {
//         console.error(`Error fetching ${column}:`, error);
//       } finally {
//         setLoading(false);
//       }
//     };
//     const loadInitialData = async () => {
//       Promise.all([
//         fetchDropdownData("asset_type", setAssetTypes, setLoadingAssetTypes, CACHE_KEYS.ASSET_TYPES),
//         fetchDropdownData("currency", setCurrencies, setLoadingCurrencies, CACHE_KEYS.CURRENCIES),
//         // UPDATED this part to use the new API function
//         fetchAllUsersForMappingAPI()
//           .then((mappedUsers) => setUsers(mappedUsers))
//           .catch((err) => {
//             console.error(
//               "Failed to fetch user list. 'Added by' column may show IDs.",
//               err
//             );
//             setUsers([]); // Fallback to an empty array on error
//           }),
//       ]);
//     };
//     loadInitialData();
//   }, []);

//   const displayedAssets = useMemo(() => {
//     if (!Array.isArray(assetsList)) return [];
//     if (!searchTerm) return assetsList.slice(0, 10);
//     const lowercasedFilter = searchTerm.toLowerCase();
//     return assetsList.filter((asset) =>
//       Object.values(asset).some((value) =>
//         String(value).toLowerCase().includes(lowercasedFilter)
//       )
//     );
//   }, [assetsList, searchTerm]);

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     const val = type === "checkbox" ? checked : value;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: name === "is_sold" || name === "active" ? val === "true" : val,
//     }));
//   };

//   const resetFormState = () => {
//     setFormData(initialFormData);
//     setSelectedFiles([]);
//     setFileDescriptions([]);
//     setExistingAttachments([]);
//     setSubmitStatus(null);
//     setFormMode("create");
//     setEditingAssetId(null);
//     const fileInput = document.getElementById("image_upload");
//     if (fileInput) fileInput.value = "";
//   };

//   const handleShowCreateForm = () => {
//     resetFormState();
//     setIsFormVisible(true);
//     setTimeout(
//       () =>
//         formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
//       100
//     );
//   };

//   const handleEditClick = async (asset) => {
//     resetFormState();
//     setFormMode("edit");
//     setEditingAssetId(asset.id);
//     setFormData({
//       ...initialFormData,
//       ...asset,
//       date_of_purchase: asset.date_of_purchase
//         ? asset.date_of_purchase.split("T")[0]
//         : "",
//       active: asset.active === undefined ? true : asset.active,
//     });
//     setIsFormVisible(true);
//     try {
//       const response = await fetch(
//         `${ATTACHMENT_API_URL}/attachment/api/attachments/${asset.id}/Assets`,
//         { headers: getApiHeaders() }
//       );
//       if (response.ok) {
//         const attachments = await response.json();
//         setExistingAttachments(attachments.filter((att) => att.active));
//       }
//     } catch (error) {
//       console.error("Error fetching attachments:", error);
//     }
//     setTimeout(
//       () =>
//         formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
//       100
//     );
//   };

//   const handleDeleteExistingAttachment = async (attachmentId) => {
//     if (!window.confirm("Are you sure?")) return;
//     try {
//       await deleteAttachmentAPI(attachmentId);
//       setExistingAttachments((prev) =>
//         prev.filter((att) => att.id !== attachmentId)
//       );
//       setSubmitStatus({ type: "success", message: "File deleted." });
//       setTimeout(() => setSubmitStatus(null), 3000);
//     } catch (error) {
//       setSubmitStatus({ type: "error", message: error.message });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsLoading(true);
//     setSubmitStatus(null);
//     const uploadFiles = async (assetId) => {
//       if (selectedFiles.length === 0) return;
//       const uploadFormData = new FormData();
//       selectedFiles.forEach((file) => uploadFormData.append("files", file));
//       fileDescriptions.forEach((desc) =>
//         uploadFormData.append("descriptions[]", desc || "")
//       );
//       try {
//         const response = await fetch(
//           `${ATTACHMENT_API_URL}/attachment/api/attachments/${assetId}/Assets`,
//           { method: "POST", headers: getApiHeaders(true), body: uploadFormData }
//         );
//         if (!response.ok) {
//           const errorData = await response.json().catch(() => ({}));
//           throw new Error(errorData.message || "File upload failed.");
//         }
//         setSelectedFiles([]);
//         setFileDescriptions([]);
//       } catch (uploadError) {
//         setSubmitStatus({
//           type: "error",
//           message: `Asset saved, but file upload failed: ${uploadError.message}`,
//         });
//         throw uploadError;
//       }
//     };
//     const payload = {
//       ...formData,
//       year_of_purchase: formData.year_of_purchase
//         ? Number(formData.year_of_purchase)
//         : null,
//       year_of_manufacture: formData.year_of_manufacture
//         ? Number(formData.year_of_manufacture)
//         : null,
//       capacity_mt: formData.capacity_mt ? Number(formData.capacity_mt) : null,
//       purchase_cost: formData.purchase_cost
//         ? Number(formData.purchase_cost)
//         : null,
//       written_down_value: formData.written_down_value
//         ? Number(formData.written_down_value)
//         : null,
//       date_of_purchase: formData.date_of_purchase || null,
//     };
//     Object.keys(payload).forEach((key) => {
//       if (payload[key] === "" || payload[key] === null) {
//         delete payload[key];
//       }
//     });
//     let assetId = formMode === "edit" ? editingAssetId : null;
//     try {
//       if (formMode === "create") {
//         const response = await fetch(`${ASSET_API_URL}/assets/`, {
//           method: "POST",
//           headers: getApiHeaders(),
//           body: JSON.stringify(payload),
//         });
//         const responseData = await response.json();
//         if (!response.ok || response.status !== 201)
//           throw new Error(
//             responseData.message ||
//               responseData.detail ||
//               "Failed to create asset."
//           );
//         assetId = responseData.id;
//       } else {
//         const response = await fetch(
//           `${ASSET_API_URL}/assets/${editingAssetId}`,
//           {
//             method: "PUT",
//             headers: getApiHeaders(),
//             body: JSON.stringify(payload),
//           }
//         );
//         if (!response.ok) {
//           const errorData = await response.json();
//           throw new Error(
//             errorData.message || errorData.detail || "Failed to update asset."
//           );
//         }
//       }
//       if (selectedFiles.length > 0) {
//         await uploadFiles(assetId);
//       }
//       setSubmitStatus({
//         type: "success",
//         message: `Asset ${
//           formMode === "create" ? "created" : "updated"
//         } successfully!`,
//       });
//       resetFormState();
//       // Clear assets cache and refetch to get fresh data
//       cache.clear(CACHE_KEYS.ASSETS);
//       await fetchAssets(false); // Force fresh fetch without cache
//       setIsFormVisible(false);
//     } catch (error) {
//       setSubmitStatus({
//         type: "error",
//         message: `Submission failed: ${error.message}`,
//       });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleFormActionCancel = () => {
//     setIsFormVisible(false);
//     resetFormState();
//   };
//   const getFileNameFromUrl = (url) => {
//     try {
//       return decodeURIComponent(url.split("/").pop());
//     } catch {
//       return url;
//     }
//   };
//   const formatDate = (dateString) =>
//     dateString ? dateString.split("T")[0] : "N/A";
//   const exportToCsv = (assetsToExport, filenamePrefix) => {
//     if (assetsToExport.length === 0) {
//       alert("No data available to export.");
//       return;
//     }
//     const sanitize = (field) => {
//       const str = String(field ?? "");
//       return str.includes(",") || str.includes('"') || str.includes("\n")
//         ? `"${str.replace(/"/g, '""')}"`
//         : str;
//     };
//     const headers = [
//       "ID",
//       "Asset Code",
//       "Name",
//       "Asset Type",
//       "Description",
//       "Reg Number",
//       "Serial Number",
//       "Year Purchase",
//       "Year Manufacture",
//       "Capacity (MT)",
//       "Purchase Cost",
//       "WDV",
//       "Purchase Date",
//       "Supplier",
//       "Currency",
//       "Avg KM/L",
//       "Avg HR/L",
//       "Is Sold",
//       "Active",
//     ];
//     const csvRows = assetsToExport.map((a) =>
//       [
//         a.id,
//         a.asset_code,
//         a.name,
//         a.asset_type,
//         a.description,
//         a.registration_number,
//         a.serial_number,
//         a.year_of_purchase,
//         a.year_of_manufacture,
//         a.capacity_mt,
//         a.purchase_cost,
//         a.written_down_value,
//         formatDate(a.date_of_purchase),
//         a.supplier,
//         a.currency,
//         a.avg_km_per_liter,
//         a.avg_hr_per_liter,
//         a.is_sold ? "Yes" : "No",
//         a.active ? "Active" : "Inactive",
//       ]
//         .map(sanitize)
//         .join(",")
//     );
//     const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
//       type: "text/csv;charset=utf-8;",
//     });
//     const link = document.createElement("a");
//     link.href = URL.createObjectURL(blob);
//     link.download = `${filenamePrefix}_${new Date()
//       .toISOString()
//       .slice(0, 10)}.csv`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };
//   const handleExportFiltered = () =>
//     exportToCsv(displayedAssets, "assets_export_filtered");
//   const handleExportAll = () => exportToCsv(assetsList, "assets_export_all");

//   // The rest of the component remains unchanged
//   return (
//     <div className="assets-container">
//       <div className="assets-list-container">
//         <div className="list-header">
//           <h2 className="assets-list-title">
//             {searchTerm ? "Search Results" : "Recently Created Assets"}
//           </h2>
//           <div className="list-actions">
//             <button onClick={handleShowCreateForm} className="btn-action">
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 viewBox="0 0 20 20"
//                 fill="currentColor"
//                 width="20"
//                 height="20"
//               >
//                 <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
//               </svg>
//               Create New Asset
//             </button>
//             <button
//               onClick={handleExportFiltered}
//               className="btn-action"
//               disabled={loadingAssetsList || displayedAssets.length === 0}
//               title="Export currently visible results"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 viewBox="0 0 20 20"
//                 fill="currentColor"
//                 width="20"
//                 height="20"
//               >
//                 <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
//                 <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
//               </svg>
//               Export Filtered
//             </button>
//             <button
//               onClick={handleExportAll}
//               className="btn-action"
//               disabled={loadingAssetsList || assetsList.length === 0}
//               title="Export all assets"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 viewBox="0 0 20 20"
//                 fill="currentColor"
//                 width="20"
//                 height="20"
//               >
//                 <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
//                 <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
//               </svg>
//               Export All
//             </button>
//           </div>
//         </div>
//         <div className="list-search-bar">
//           <input
//             type="text"
//             placeholder="Search all columns..."
//             className="search-input"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>
//         {submitStatus && (
//           <div className={`status-message ${submitStatus.type}`}>
//             {submitStatus.message}
//           </div>
//         )}
//         {loadingAssetsList ? (
//           <p>Loading assets...</p>
//         ) : (
//           <div className="table-wrapper">
//             <table className="assets-table">
//               <thead>
//                 <tr>
//                   <th>Actions</th>
//                   <th>Asset Code</th>
//                   <th>Name</th>
//                   <th>Type</th>
//                   <th>Status</th>
//                   <th>Reg. No</th>
//                   <th>Serial No</th>
//                   <th className="description-cell">Description</th>
//                   <th>Cost</th>
//                   <th>Purchase Date</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {displayedAssets.length > 0 ? (
//                   displayedAssets.map((asset) => (
//                     <tr key={asset.id}>
//                       <td>
//                         <button
//                           onClick={() => handleEditClick(asset)}
//                           className="btn-action btn-action-sm"
//                           disabled={isLoading}
//                         >
//                           Edit
//                         </button>
//                       </td>
//                       <td>{asset.asset_code || "N/A"}</td>
//                       <td>{asset.name || "N/A"}</td>
//                       <td>{asset.asset_type || "N/A"}</td>
//                       <td>
//                         <span
//                           className={
//                             asset.active ? "status-active" : "status-inactive"
//                           }
//                         >
//                           {asset.active ? "Active" : "Inactive"}
//                         </span>
//                       </td>
//                       <td>{asset.registration_number || "N/A"}</td>
//                       <td>{asset.serial_number || "N/A"}</td>
//                       <td className="description-cell">
//                         {asset.description || "N/A"}
//                       </td>
//                       <td>
//                         {asset.purchase_cost
//                           ? `${
//                               asset.currency || ""
//                             } ${asset.purchase_cost.toLocaleString()}`
//                           : "N/A"}
//                       </td>
//                       <td>{formatDate(asset.date_of_purchase)}</td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="10">
//                       {searchTerm
//                         ? "No assets match your search."
//                         : "No assets found."}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>

//       {isFormVisible && (
//         <>
//           <header className="assets-header" ref={formRef}>
//             <h1 className="assets-title">
//               {formMode === "create" ? "Create New Asset" : "Edit Asset"}
//             </h1>
//             <p className="assets-subtitle">
//               {formMode === "create"
//                 ? "Fill in the details below to add a new asset."
//                 : `You are editing: ${formData.name || ""}`}
//             </p>
//           </header>
//           <div className="assets-form-container">
//             <form onSubmit={handleSubmit} className="assets-form" noValidate>
//               <div className="form-grid">
//                 <div className="form-group">
//                   <label htmlFor="asset_code" className="form-label">
//                     Asset Code <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="asset_code"
//                     name="asset_code"
//                     value={formData.asset_code}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                     placeholder="e.g., ASSET-001"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="name" className="form-label">
//                     Asset Name <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="name"
//                     name="name"
//                     value={formData.name}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                     placeholder="Enter Asset Name"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="asset_type" className="form-label">
//                     Asset Type <span className="required-asterisk">*</span>
//                   </label>
//                   <select
//                     id="asset_type"
//                     name="asset_type"
//                     value={formData.asset_type}
//                     onChange={handleInputChange}
//                     className="form-select"
//                     required
//                     disabled={isLoading || loadingAssetTypes}
//                   >
//                     <option value="" disabled>
//                       {loadingAssetTypes ? "Loading..." : "Select Type"}
//                     </option>
//                     {assetTypes.map((type) => (
//                       <option key={type.id} value={type.name}>
//                         {type.name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div className="form-group grid-column-span-3">
//                   <label htmlFor="description" className="form-label">
//                     Description
//                   </label>
//                   <textarea
//                     id="description"
//                     name="description"
//                     value={formData.description}
//                     onChange={handleInputChange}
//                     className="form-textarea"
//                     rows="3"
//                     disabled={isLoading}
//                     placeholder="Provide a brief description"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="registration_number" className="form-label">
//                     Registration Number{" "}
//                     <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="registration_number"
//                     name="registration_number"
//                     value={formData.registration_number}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     required
//                     placeholder="e.g., MH12AB3456"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="serial_number" className="form-label">
//                     Serial Number <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="serial_number"
//                     name="serial_number"
//                     value={formData.serial_number}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     required
//                     placeholder="e.g., SN-12345XYZ"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="year_of_manufacture" className="form-label">
//                     Year of Manufacture{" "}
//                     <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="year_of_manufacture"
//                     name="year_of_manufacture"
//                     value={formData.year_of_manufacture}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     required
//                     placeholder="e.g., 2020"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="year_of_purchase" className="form-label">
//                     Year of Purchase{" "}
//                     <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="year_of_purchase"
//                     name="year_of_purchase"
//                     value={formData.year_of_purchase}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     placeholder="e.g., 2021"
//                     required
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="purchase_cost" className="form-label">
//                     Purchase Cost <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="purchase_cost"
//                     name="purchase_cost"
//                     value={formData.purchase_cost}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                     placeholder="e.g., 500000"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="currency" className="form-label">
//                     Currency
//                   </label>
//                   <select
//                     id="currency"
//                     name="currency"
//                     value={formData.currency}
//                     onChange={handleInputChange}
//                     className="form-select"
//                     disabled={isLoading || loadingCurrencies}
//                   >
//                     <option value="" disabled>
//                       {loadingCurrencies ? "Loading..." : "Select"}
//                     </option>
//                     {currencies.map((c) => (
//                       <option key={c.id} value={c.name}>
//                         {c.name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="date_of_purchase" className="form-label">
//                     Date of Purchase{" "}
//                     <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="date"
//                     id="date_of_purchase"
//                     name="date_of_purchase"
//                     value={formData.date_of_purchase}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="written_down_value" className="form-label">
//                     Written Down Value (WDV)
//                   </label>
//                   <input
//                     type="number"
//                     id="written_down_value"
//                     name="written_down_value"
//                     value={formData.written_down_value}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     placeholder="e.g., 450000"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="supplier" className="form-label">
//                     Supplier <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     id="supplier"
//                     name="supplier"
//                     value={formData.supplier}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     required
//                     placeholder="e.g., ABC Corporation"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="capacity_mt" className="form-label">
//                     Capacity (MT) <span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="capacity_mt"
//                     name="capacity_mt"
//                     value={formData.capacity_mt}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     disabled={isLoading}
//                     required
//                     placeholder="e.g., 20.5"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="avg_km_per_liter" className="form-label">
//                     Avg KM/Liter<span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="avg_km_per_liter"
//                     name="avg_km_per_liter"
//                     value={formData.avg_km_per_liter}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                     placeholder="e.g., 15.5"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="avg_hr_per_liter" className="form-label">
//                     Avg HR/Liter<span className="required-asterisk">*</span>
//                   </label>
//                   <input
//                     type="number"
//                     id="avg_hr_per_liter"
//                     name="avg_hr_per_liter"
//                     value={formData.avg_hr_per_liter}
//                     onChange={handleInputChange}
//                     className="form-input"
//                     required
//                     disabled={isLoading}
//                     placeholder="e.g., 2.5"
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="is_sold" className="form-label">
//                     Is Sold
//                   </label>
//                   <select
//                     id="is_sold"
//                     name="is_sold"
//                     value={formData.is_sold.toString()}
//                     onChange={handleInputChange}
//                     className="form-select"
//                     disabled={isLoading}
//                   >
//                     <option value="false">No</option>
//                     <option value="true">Yes</option>
//                   </select>
//                 </div>
//                 <div className="form-group">
//                   <label htmlFor="active" className="form-label">
//                     Active Status
//                   </label>
//                   <select
//                     id="active"
//                     name="active"
//                     value={formData.active.toString()}
//                     onChange={handleInputChange}
//                     className="form-select"
//                     disabled={isLoading}
//                   >
//                     <option value="true">Active</option>
//                     <option value="false">Inactive</option>
//                   </select>
//                 </div>

//                 <div className="form-group grid-column-span-3">
//                   {formMode === "edit" && (
//                     <div className="attachment-section">
//                       <h3 className="attachment-section-title">
//                         Manage Existing Attachments
//                       </h3>
//                       {existingAttachments.length > 0 ? (
//                         <div className="attachments-table-container">
//                           <table className="attachments-table">
//                             <thead>
//                               <tr>
//                                 <th>File Name</th>
//                                 <th>Description</th>
//                                 <th>Added By</th>
//                                 <th>Actions</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {existingAttachments.map((att) => (
//                                 <tr key={att.id}>
//                                   <td>
//                                     <a
//                                       href={att.file_url}
//                                       target="_blank"
//                                       rel="noopener noreferrer"
//                                       className="attachment-link"
//                                     >
//                                       <IoAttach />{" "}
//                                       {getFileNameFromUrl(att.file_url)}
//                                     </a>
//                                   </td>
//                                   <td>{att.description || ""}</td>
//                                   <td>
//                                     {userMap[att.added_by] ||
//                                       `ID: ${att.added_by}`}
//                                   </td>
//                                   <td>
//                                     <button
//                                       type="button"
//                                       className="remove-file-btn"
//                                       onClick={() =>
//                                         handleDeleteExistingAttachment(att.id)
//                                       }
//                                       title="Remove attachment"
//                                       disabled={isLoading}
//                                     >
//                                       <IoTrashOutline />
//                                     </button>
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       ) : (
//                         <p className="no-attachments-message">
//                           No existing attachments.
//                         </p>
//                       )}
//                     </div>
//                   )}

//                   <div className="attachment-section">
//                     <h3 className="attachment-section-title">
//                       {formMode === "edit"
//                         ? "Add New Attachments"
//                         : "Attachments"}
//                     </h3>
//                     <label
//                       htmlFor="image_upload"
//                       className={`drop-zone ${isDragging ? "drag-over" : ""}`}
//                       onDrop={handleFileDrop}
//                       onDragOver={handleDragOver}
//                       onDragEnter={handleDragEnter}
//                       onDragLeave={handleDragLeave}
//                     >
//                       <input
//                         id="image_upload"
//                         type="file"
//                         multiple
//                         accept="image/*,.pdf,.doc,.docx"
//                         onChange={handleFileChange}
//                         className="file-input-hidden"
//                         disabled={isLoading}
//                       />
//                       <div className="drop-zone-content">
//                         <IoCloudUploadOutline />{" "}
//                         <span>
//                           {isDragging
//                             ? "Drop files here"
//                             : "Drag & Drop or Click to Upload"}
//                         </span>
//                       </div>
//                     </label>
//                     {selectedFiles.length > 0 && (
//                       <div className="file-description-list">
//                         <h4 className="file-list-header">
//                           New Files to Upload
//                         </h4>
//                         {selectedFiles.map((file, index) => (
//                           <div
//                             key={`${file.name}-${index}`}
//                             className="file-description-item"
//                           >
//                             <span className="file-name-label" title={file.name}>
//                               {file.name}
//                             </span>
//                             <input
//                               type="text"
//                               value={fileDescriptions[index]}
//                               onChange={(e) =>
//                                 handleDescriptionChange(index, e.target.value)
//                               }
//                               placeholder="Enter a brief description..."
//                               className="file-description-input"
//                               disabled={isLoading}
//                             />
//                             <button
//                               type="button"
//                               className="remove-file-btn"
//                               onClick={() => handleRemoveNewFile(index)}
//                               title="Remove file"
//                               disabled={isLoading}
//                             >
//                               <IoTrashOutline />
//                             </button>
//                           </div>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//               <div className="form-actions">
//                 <button
//                   type="button"
//                   onClick={handleFormActionCancel}
//                   className="btn-secondary"
//                   disabled={isLoading}
//                 >
//                   {formMode === "create" ? "Reset" : "Cancel Edit"}
//                 </button>
//                 <button
//                   type="submit"
//                   className="btn-primary"
//                   disabled={isLoading}
//                 >
//                   {isLoading ? (
//                     <>
//                       {" "}
//                       <span className="loading-spinner"></span>Submitting...
//                     </>
//                   ) : formMode === "create" ? (
//                     "Create Asset"
//                   ) : (
//                     "Update Asset"
//                   )}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default Assets;








